import React, { Component, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import { throttle } from 'lodash';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Form, Row, Col } from 'reactstrap';
import DriveFile from 'react-md-icon/dist/BaselineInsertDriveFile';
import { Formik } from 'formik';

import {
  displayToaster,
  hideToaster, toggleDrawer,
  GET_DOCUMENT_PREVIEW_URL,
  clearLoadingState,
  UPDATE_EMPLOYEE_DOCUMENT, GET_UPLOAD_URL, UPDATE_COMPANY_DOCUMENT, DELETE_COMPANY_DOCUMENT,
  DELETE_EMPLOYEE_DOCUMENT, LOADING_PROP_TYPE, GET_EMPLOYEE_DOCUMENT_CATEGORIES,
  clearCategories, getEditTemplateUrl,
  GET_COMPANY_DOCUMENT_CATEGORIES,
} from '../../../modules';
import {
  Drawer,
  UnsupportedError,
  LockedModalBody,
  FileViewer,
  ExitConfirmationModal,
} from '../../../components';
import {
  APPLICATION_ROOT,
  PENDING_STEP_NUMBER,
  ROLE_BASE_ADMIN,
} from '../../../constants';
import { downloadFromUrl, setFileUrl, checkRole } from '../../../helpers';
import EditDocumentHeader from '../EditDocumentHeader/EditDocumentHeader';
import DetailsPane from '../DetailsPane/DetailsPane';
import './EditDocument.scss';

const DEFAULT_STATE = {
  file: undefined,
  detailsPaneIsOpen: true,
  detailsPaneHeight: undefined,
  formIsDirty: false,
  confirmationModalIsOpen: false,
  isHelloSignOpen: false,
  helloSignUrlLoaded: false,
};

class EditDocument extends Component {
  constructor(props) {
    super(props);

    this.state = DEFAULT_STATE;

    this.handleDrawer = this.handleDrawer.bind(this);
    this.throttledToggleDetails = throttle(this.toggleDetailsPane.bind(this), 500);
    this.resetTemplateState = this.resetTemplateState.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const {
      loading,
      selectedDocument,
      drawerIsOpen,
      editTemplateUrl,
      docCategories: {
        categories,
      },
    } = this.props;
    const { loading: prevLoading, drawerIsOpen: prevDrawerIsOpen, editTemplateUrl: prevUrl } = prevProps;
    if (document.getElementById('edit-document-header')) {
      const headerHeight = document.getElementById('edit-document-header').clientHeight;
      const detailsPaneHeight = `calc(100vh - ${headerHeight}px)`;
      if (!this.state.detailsPaneHeight) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ detailsPaneHeight });
      }
    }
    if (prevLoading[UPDATE_EMPLOYEE_DOCUMENT] === 'loading' && loading[UPDATE_EMPLOYEE_DOCUMENT] === 'complete') {
      this.props.displayToaster([
        'success',
        <FormattedMessage id="DOCUMENT_UPDATED" />,
        'app',
        5000,
        true,
      ]);
      this.handleDrawer();
    }
    if (prevLoading[UPDATE_COMPANY_DOCUMENT] === 'loading' && loading[UPDATE_COMPANY_DOCUMENT] === 'complete') {
      this.props.displayToaster([
        'success',
        <FormattedMessage id="DOCUMENT_UPDATED" />,
        'app',
        5000,
        true,
      ]);
      if (!this.state.isHelloSignOpen) this.handleDrawer();
    }
    if ((prevLoading[DELETE_COMPANY_DOCUMENT] === 'loading' && loading[DELETE_COMPANY_DOCUMENT] === 'complete') ||
       (prevLoading[DELETE_EMPLOYEE_DOCUMENT] === 'loading' && loading[DELETE_EMPLOYEE_DOCUMENT] === 'complete')) {
      this.props.displayToaster([
        'success',
        <FormattedMessage id="DOCUMENT_DELETED" />,
        'app',
        5000,
        true,
      ]);
      this.handleDrawer();
    }
    if (editTemplateUrl && editTemplateUrl !== prevUrl) {
      this.toggleHelloSignUrlLoaded();
    }
    if (
      drawerIsOpen &&
      selectedDocument &&
      selectedDocument.id &&
      ((loading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] && loading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] !== 'loading' &&
      (!prevLoading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] || prevLoading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] === 'complete')) ||
      (loading[GET_COMPANY_DOCUMENT_CATEGORIES] && loading[GET_COMPANY_DOCUMENT_CATEGORIES] !== 'loading' &&
      (!prevLoading[GET_COMPANY_DOCUMENT_CATEGORIES] || prevLoading[GET_COMPANY_DOCUMENT_CATEGORIES] === 'complete')))
    ) {
      this.props.getDocCategoryList(undefined, selectedDocument.companyId, selectedDocument.employeeId);
    }
    if (prevDrawerIsOpen && !drawerIsOpen && categories.length > 0) {
      this.props.clearCategories();
    }
  }

  closeConfirmationModal(quitEditing, resetForm) {
    this.setState({ confirmationModalIsOpen: false });
    if (quitEditing) {
      this.handleDrawer(false, resetForm);
    } else if (!this.state.detailsPaneIsOpen) {
      this.toggleDetailsPane();
    }
  }

  toggleHelloSign() {
    this.setState({ isHelloSignOpen: true });
  }

  toggleHelloSignUrlLoaded = () => {
    this.setState({ helloSignUrlLoaded: true });
  }

  handleDrawer(isDirty, resetForm) {
    const { drawerIsOpen } = this.props;
    if (isDirty) {
      this.setState({ confirmationModalIsOpen: true });
    } else {
      this.props.toggleDrawer(!drawerIsOpen);
      this.props.hideToaster('drawer');
      this.props.clearLoadingState(UPDATE_EMPLOYEE_DOCUMENT);
      this.props.clearLoadingState(UPDATE_COMPANY_DOCUMENT);
      if (resetForm && typeof resetForm === 'function') {
        resetForm();
      }
      // Allowing the drawer to close before resetting state makes for a cleaner animation
      setTimeout(() => this.setState(DEFAULT_STATE), 500);
    }
  }

  toggleDetailsPane() {
    this.setState({ detailsPaneIsOpen: !this.state.detailsPaneIsOpen });
  }

  resetTemplateState() {
    this.setState(DEFAULT_STATE);
  }

  render() {
    const {
      loading,
      drawerIsOpen,
      selectedDocument,
      filePreview: { url, mimeType },
      userRoles,
      isManagerOrAbove,
      isFormLocked,
      onSubmit,
      isDeleteButtonDisabled,
      formData,
      deleteAction,
      deleteQuestionDetail,
      editTemplateData,
    } = this.props;
    const {
      file,
      detailsPaneIsOpen,
      detailsPaneHeight,
    } = this.state;
    const isSubmitting =
      loading[UPDATE_COMPANY_DOCUMENT] === 'loading' ||
      loading[UPDATE_EMPLOYEE_DOCUMENT] === 'loading' ||
      loading[GET_UPLOAD_URL] === 'loading';
    const submitDisabled = loading[GET_DOCUMENT_PREVIEW_URL] === 'error';
    const containerClasses = ['w-100', 'h-100'];
    const largeIconStyles = { fontSize: '5rem', color: 'var(--color-neutral-500)', width: '100%' };
    const isLoading = loading[GET_DOCUMENT_PREVIEW_URL] === 'loading';
    if (isLoading) {
      containerClasses.push('hello-sign-container');
      containerClasses.push('loading');
    }

    const fileUrl = setFileUrl(url);

    let documentPreview;
    const previewRowClasses = new Set(['align-items-center', 'p-3', 'd-inline-block', 'col']);
    const previewColClasses = new Set(['h-100', 'p-0']);
    if (!isLoading) {
      previewColClasses.add('border-dashed');
      if (selectedDocument && selectedDocument.status && selectedDocument.status.stepNumber === PENDING_STEP_NUMBER) {
        previewColClasses.delete('border-dashed');
        containerClasses.push('d-flex', 'align-items-center', 'justify-content-center', 'pb-5');
        documentPreview = (
          <div>
            <DriveFile style={largeIconStyles} />
            <h5 className="text-center pt-3"><FormattedMessage id="PREVIEW_UNAVAILABLE" /></h5>
            <div className="text-center text-muted"><FormattedMessage id="PREVIEW_UNAVAILABLE_PENDING" /></div>
          </div>
        );
      } else if (selectedDocument && selectedDocument.status && selectedDocument.status.isProcessing === true) {
        previewColClasses.delete('border-dashed');
        containerClasses.push('d-flex', 'align-items-center', 'justify-content-center', 'pb-5');
        documentPreview = (
          <div>
            <DriveFile style={largeIconStyles} />
            <h5 className="text-center pt-3"><FormattedMessage id="PREVIEW_UNAVAILABLE" /></h5>
            <div className="text-center text-muted"><FormattedMessage id="PREVIEW_UNAVAILABLE_PROCESSING" /></div>
            <div className="text-center text-muted"><FormattedMessage id="PLEASE_TRY_AGAIN" /></div>
          </div>
        );
      } else {
        documentPreview = (
          <Fragment>
            <FileViewer
              fileType={(mimeType && mimeType.replace('.', '')) || ''}
              filePath={fileUrl || ''}
              unsupportedComponent={
                () => (
                  <UnsupportedError
                    messageId="FILE_COULD_NOT_BE_PREVIEWED"
                    ctaMessageId="DOWNLOAD"
                    onClick={() => downloadFromUrl(fileUrl, selectedDocument.filename)}
                    classes="p-3"
                  />
                )
              }
              loadingComponent={() => (
                <div className="file-viewer-loading d-flex h-100 w-100" />
              )}
            />
          </Fragment>
        );
      }
    }

    return (
      <Formik
        initialValues={formData.initialValues}
        validateOnChange
        validateOnBlur
        validationSchema={formData.validationSchema}
        validate={values => !values.title || !file}
      >
        {({
          values,
          errors,
          touched,
          dirty,
          resetForm,
        }) => (
          <Drawer
            modalClassName="drawer wider"
            toggle={this.handleDrawer.bind(this, dirty)}
            isOpen={drawerIsOpen}
            parentselector={APPLICATION_ROOT}
            backdrop={isLoading ? 'static' : true}
          >
            <Fragment>
              <ExitConfirmationModal
                closeConfirmationModal={quitEditing => this.closeConfirmationModal(quitEditing, resetForm)}
                confirmationModalIsOpen={this.state.confirmationModalIsOpen}
                title="DISCARD_CHANGES"
                message="YOUR_CHANGES_WONT_BE_SAVED"
                cancelText="CANCEL"
                discardText="DISCARD"
                submittingText="DISCARDING"
              />
              <EditDocumentHeader
                toggle={this.handleDrawer.bind(this, dirty)}
                isAdminOrAbove={this.props.isAdminOrAbove}
                downloadAction={
                // Download button shouldn't be present for pending or processing documents
                // NOTE: Company docs don't have 'status', this doesn't work for them
                selectedDocument.status &&
                (selectedDocument.status.stepNumber === PENDING_STEP_NUMBER ||
                selectedDocument.status.isProcessing === true) ?
                    undefined
                    :
                    () => downloadFromUrl(fileUrl, selectedDocument.filename)
              }
                toggleDetailsPane={() => this.throttledToggleDetails()}
                isDeleteButtonDisabled={isDeleteButtonDisabled}
                selectedDocument={selectedDocument}
                isManagerOrAbove={isManagerOrAbove}
                deleteAction={deleteAction}
                deleteQuestionDetail={deleteQuestionDetail}
              />
              <LockedModalBody id="edit-document-body" className="p-0 d-flex flex-column overflow-hidden" ref={this.wrapperRef}>
                <Form>
                  <Row className="m-0">
                    <Col
                      key="file-viewer"
                      id="file-viewer"
                      style={detailsPaneHeight ? { height: detailsPaneHeight } : { height: '93vh' }}
                      className={`overflow-auto ${[...previewRowClasses].join(' ')}`}
                    >
                      <Col className={[...previewColClasses].join(' ')}>
                        <div className={containerClasses.join(' ')}>
                          {documentPreview}
                        </div>
                      </Col>
                    </Col>

                    <DetailsPane
                      height={detailsPaneHeight}
                      file={file}
                      submitDisabled={submitDisabled}
                      signatureTypeLabel={this.props.signatureTypeLabel}
                      isOpen={detailsPaneIsOpen}
                      selectedDocument={selectedDocument}
                      isFormLocked={isFormLocked}
                      isSubmitting={isSubmitting}
                      userRoles={userRoles}
                      toggle={() => this.throttledToggleDetails()}
                      docCategories={this.props.docCategories}
                      loading={loading}
                      onSubmit={formValues => onSubmit(formValues)}
                      values={values}
                      touched={touched}
                      errors={errors}
                      formFields={this.props.formFields}
                      props={this.props}
                      state={this.state}
                      dirty={dirty}
                      toggleHelloSign={() => this.toggleHelloSign()}
                      isHelloSignOpen={this.state.isHelloSignOpen}
                      resetTemplateState={this.resetTemplateState}
                      editTemplateData={editTemplateData}
                      helloSignUrlLoaded={this.state.helloSignUrlLoaded}
                      getEditTemplateUrl={this.props.getEditTemplateUrl}
                    />
                  </Row>
                </Form>
              </LockedModalBody>
            </Fragment>
          </Drawer>
        )}
      </Formik>


    );
  }
}

EditDocument.defaultProps = {
  drawerIsOpen: false,
  selectedDocument: {},
  docCategories: undefined,
  isManagerOrAbove: false,
  isAdminOrAbove: false,
  signatureTypeLabel: null,
  deleteAction: null,
  deleteQuestionDetail: null,
  getEditTemplateUrl: null,
  editTemplateUrl: null,
};

EditDocument.propTypes = {
  displayToaster: PropTypes.func.isRequired,
  loading: LOADING_PROP_TYPE.isRequired,
  drawerIsOpen: PropTypes.bool,
  toggleDrawer: PropTypes.func.isRequired,
  hideToaster: PropTypes.func.isRequired,
  clearLoadingState: PropTypes.func.isRequired,
  getDocCategoryList: PropTypes.func.isRequired,
  clearCategories: PropTypes.func.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  isManagerOrAbove: PropTypes.bool,
  isAdminOrAbove: PropTypes.bool,
  isFormLocked: PropTypes.bool.isRequired,
  isDeleteButtonDisabled: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editTemplateUrl: PropTypes.string,
  editTemplateData: PropTypes.shape({
    url: PropTypes.string,
    clientId: PropTypes.string,
  }).isRequired,
  filePreview: PropTypes.shape({
    url: PropTypes.string,
    filename: PropTypes.string,
  }).isRequired,
  selectedDocument: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    filename: PropTypes.string,
    category: PropTypes.string,
    uploadDate: PropTypes.string,
    extension: PropTypes.string,
    isPublishedToEmployee: PropTypes.bool,
    companyId: PropTypes.number,
    companyName: PropTypes.string,
    employeeId: PropTypes.number,
    employeeName: PropTypes.string,
    isOnboarding: PropTypes.bool,
    uploadedBy: PropTypes.string,
    isLegacyDocument: PropTypes.bool,
    isEsignatureDocument: PropTypes.bool,
    isHelloSignDocument: PropTypes.bool,
    isHelloSignTemplate: PropTypes.bool,
    isPrivate: PropTypes.bool,
    status: PropTypes.shape({
      name: PropTypes.string,
      priority: PropTypes.number,
      stepNumber: PropTypes.number,
      isProcessing: PropTypes.bool,
    }),
  }),
  formData: PropTypes.shape({
    initialValues: PropTypes.shape({
      title: PropTypes.string,
      visibility: PropTypes.bool,
    }),
    validationSchema: PropTypes.shape({
      title: PropTypes.string,
    }),
  }).isRequired,
  docCategories: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    recent: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    next: PropTypes.string,
  }),
  formFields: PropTypes.arrayOf(PropTypes.any).isRequired,
  signatureTypeLabel: PropTypes.string,
  deleteAction: PropTypes.func,
  deleteQuestionDetail: PropTypes.string,
  getEditTemplateUrl: PropTypes.func,
};

export default connect(
  (state) => {
    const {
      resources: {
        templates: {
          editTemplate,
        },
        docs: {
          filePreview,
          selectedDocument,
          docCategories,
        },
      },
      appState,
      user: {
        roles,
      },
    } = state;
    return {
      loading: appState.loading,
      drawerIsOpen: appState.drawer.isOpen,
      userRoles: roles,
      isAdminOrAbove: checkRole(roles, ROLE_BASE_ADMIN),
      filePreview,
      selectedDocument,
      docCategories,
      editTemplateData: editTemplate,
    };
  },
  {
    displayToaster: args => displayToaster(...args),
    toggleDrawer: open => toggleDrawer(open),
    hideToaster: location => hideToaster(location),
    clearLoadingState: action => clearLoadingState(action),
    clearCategories,
    getEditTemplateUrl,
  }
)(hot(module)(EditDocument));



// WEBPACK FOOTER //
// ./src/containers/EditDocument/EditDocument/EditDocument.jsx